import type { FeedSignal, LifecycleStatus } from "./types";

/**
 * Internal pursuit-stage model (Item 3 · backend payoff wiring).
 *
 * get_signal_detail now projects the canonical pursuit_stage — rebuilt
 * server-side by bdse_rebuild_signal_lifecycle_projection from the ordered
 * ledger, including stage_regressed walk-backs — so the modal reads the
 * projection via internalStageFromProjection rather than re-deriving the reached
 * stage from action_history. A null pursuit_stage is the Assigned base; a won
 * lifecycle completes the ladder.
 */

export const INTERNAL_STAGE_ORDER = [
  "assigned",
  "outreach_made",
  "meeting",
  "proposal",
  "won",
] as const;

export type InternalStageId = (typeof INTERNAL_STAGE_ORDER)[number];

/** Ledger event → the stage it moves the pursuit into. */
const EVENT_TO_STAGE: Record<string, InternalStageId> = {
  stage_outreach_made: "outreach_made",
  stage_meeting_scheduled: "meeting",
  stage_proposal: "proposal",
};

export type StageEventType =
  | "stage_outreach_made"
  | "stage_meeting_scheduled"
  | "stage_proposal";

/** Valid stage_regressed to_stage targets (earlier than proposal). */
export type WalkBackTarget = "assigned" | "stage_outreach_made" | "stage_meeting_scheduled";

/** Current stage → the forward event that records the next transition. */
export const NEXT_STAGE_EVENT: Partial<Record<InternalStageId, StageEventType>> = {
  assigned: "stage_outreach_made",
  outreach_made: "stage_meeting_scheduled",
  meeting: "stage_proposal",
};

/**
 * Current stage → the stage event to walk back to (stage_regressed to_stage).
 * proposal → meeting, meeting → outreach_made, outreach_made → assigned.
 */
export const PREV_STAGE_EVENT: Partial<Record<InternalStageId, WalkBackTarget>> = {
  outreach_made: "assigned",
  meeting: "stage_outreach_made",
  proposal: "stage_meeting_scheduled",
};

export const INTERNAL_STAGE_LABELS: Record<InternalStageId, string> = {
  assigned: "Assigned or claimed",
  outreach_made: "Outreach made",
  meeting: "Meeting scheduled",
  proposal: "Proposal",
  won: "Won",
};

/** Label for a stage event id (used by walk-back copy + confirm dialogs). */
export function stageEventLabel(event: string | null | undefined): string | null {
  if (!event) return null;
  if (event === "assigned") return INTERNAL_STAGE_LABELS.assigned;
  const stage = EVENT_TO_STAGE[event];
  return stage ? INTERNAL_STAGE_LABELS[stage] : null;
}

/** True when the signal's lifecycle is a terminal (closed) pursuit. */
export function isClosedPursuit(lifecycleStatus: LifecycleStatus): boolean {
  return lifecycleStatus === "won" || lifecycleStatus === "passed";
}

/**
 * Closed outcome derived from lifecycle_status — the same mapping the server's
 * toFeedRow uses for closed_outcome. lifecycle_status is the projection-backed
 * source, so no separate adapter field is needed.
 */
export function closedOutcomeOf(
  signal: Pick<FeedSignal, "lifecycleStatus">,
): "won" | "passed" | null {
  if (signal.lifecycleStatus === "won") return "won";
  if (signal.lifecycleStatus === "passed") return "passed";
  return null;
}

/**
 * Translate the canonical server projection into the modal's stage model (R-3).
 *
 * Reads canonical_stage_outcome.current_stage_key from get_signal_detail —
 * v_signal_stage_outcome_current, derived from the ordered ledger — in place of
 * the legacy pursuit_stage cache column plus a lifecycle_status test.
 *
 * Parity with the derivation it replaces: current_stage_key is 'won' exactly
 * when the standing ledger outcome is 'won' (the case the old code read off
 * lifecycle_status), and otherwise carries the same four effective-stage values
 * the cache held, with 'assigned' in place of null. Both are rebuilt from the
 * same ledger predicate, ordering and tiebreak.
 *
 * NOTE this deliberately no longer consults lifecycle_status: lifecycle reads
 * stay on legacy fields (out of scope for this cutover), and the archived /
 * expired / suppressed states the projection cannot express are read elsewhere,
 * unchanged.
 */
export function internalStageFromProjection(
  canonicalStageKey:
    | "assigned"
    | "stage_outreach_made"
    | "stage_meeting_scheduled"
    | "stage_proposal"
    | "won"
    | null
    | undefined,
): InternalStageId {
  if (canonicalStageKey === "won") return "won";
  if (canonicalStageKey === "stage_outreach_made") return "outreach_made";
  if (canonicalStageKey === "stage_meeting_scheduled") return "meeting";
  if (canonicalStageKey === "stage_proposal") return "proposal";
  return "assigned";
}
